import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import usePatientListExport from './usePatientListExport';
import Toast from '../../../../components/support/Toast';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';

// A lista sai com contato de todas as pessoas atendidas: só Proprietário(a) e
// Administrador(a) exportam. O servidor (patients/export_list.php) barra a Equipe;
// aqui o cadeado mostra a trava antes do clique.
const EXPORT_LOCKED_REASON = 'Só Proprietário(a) e Administrador(a) exportam a lista.';

/**
 * SidePanel - Right side panel with export options
 */
function SidePanel({ patients = [] }) {
  const navigate = useNavigate();
  const { exportExcel, exportPdf, exporting, isEmpty } = usePatientListExport(patients);
  const [toastMessage, setToastMessage] = useState('');
  const { isOwnerOrAdmin } = useRoleAccess();
  const canExport = isOwnerOrAdmin();
  const lockIcon = <LockOutlinedIcon sx={{ color: 'text.disabled' }} />;

  const handleExport = (exportFn) => async () => {
    const result = await exportFn();
    if (!result.success) setToastMessage(result.error);
  };

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        bgcolor: '#fff',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 0.75, fontSize: '0.9375rem', color: 'text.primary' }}
      >
        Exportar dados
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, fontSize: '0.8125rem', lineHeight: 1.5 }}
      >
        Exporte sua base de pacientes.
      </Typography>

      <Tooltip title={canExport ? '' : EXPORT_LOCKED_REASON} arrow>
        <Box component="span" sx={{ display: 'block', mb: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={canExport ? <TableChartOutlinedIcon sx={{ color: '#4CAF50' }} /> : lockIcon}
            onClick={handleExport(exportExcel)}
            disabled={isEmpty || !canExport || exporting}
            sx={{
              justifyContent: 'flex-start',
              borderRadius: '8px',
              borderColor: '#E8ECF0',
              color: 'text.primary',
              fontSize: '0.8125rem',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#4CAF50',
                bgcolor: 'rgba(76, 175, 80, 0.05)',
              },
            }}
          >
            Exportar Excel
          </Button>
        </Box>
      </Tooltip>

      <Tooltip title={canExport ? '' : EXPORT_LOCKED_REASON} arrow>
        <Box component="span" sx={{ display: 'block' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={canExport ? <PictureAsPdfOutlinedIcon sx={{ color: '#F44336' }} /> : lockIcon}
            onClick={handleExport(exportPdf)}
            disabled={isEmpty || !canExport || exporting}
            sx={{
              justifyContent: 'flex-start',
              borderRadius: '8px',
              borderColor: '#E8ECF0',
              color: 'text.primary',
              fontSize: '0.8125rem',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#F44336',
                bgcolor: 'rgba(244, 67, 54, 0.05)',
              },
            }}
          >
            Exportar PDF
          </Button>
        </Box>
      </Tooltip>

      {!canExport && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, lineHeight: 1.4 }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14 }} />
          {EXPORT_LOCKED_REASON}
        </Typography>
      )}

      {/* Quick tip */}
      <Box sx={{
        mt: 3,
        pt: 2,
        borderTop: '1px solid #F0F0F0',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LightbulbOutlinedIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography variant="subtitle2" fontWeight={600} color="warning.main">
            Dica rápida
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          Importe pacientes via planilha para agilizar seu cadastro.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => navigate('/system/import')}
          sx={{
            borderRadius: '8px',
            borderColor: '#E0E0E0',
            color: 'text.primary',
            fontWeight: 400,
            fontSize: '0.8125rem',
            '&:hover': { borderColor: '#BDBDBD', backgroundColor: 'transparent' },
          }}
        >
          Importar pacientes
        </Button>
      </Box>

      <Toast
        open={Boolean(toastMessage)}
        message={toastMessage}
        severity="error"
        onClose={() => setToastMessage('')}
      />
    </Paper>
  );
}

export default SidePanel;
